import MomentUtils from "@date-io/moment";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import { InfoOutlined } from "@material-ui/icons";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { BsFiletypeHtml } from "react-icons/bs";
import { FaFileUpload } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { toastError } from "../../../utils";
import HtmlImportGuide from "../../../components/Common/HtmlImportGuide";
import { DownloadPdf } from "./downloadPdf";
import { UploadPDFModal } from "./uploadPdf";

// Helper function to update the cargo operation data
const updateCargoOperationData = ({
  setCargoOperationTableData,
  selectedOperationIndex,
  field,
  value,
  setWarning = () => { },
  currentItemId = "",
}) => {
  setCargoOperationTableData((prevState) => {
    const newState = { ...prevState };
    const updatedCargoList = [...newState.cargoList];
    const currentItem = { ...updatedCargoList[selectedOperationIndex] };
    const currentLaytime = currentItem.laytimeStatement || {};

    const updatedData = {
      ...currentLaytime,
      [field]: value,
    };

    if (field === "laytimeStarts" || field === "laytimeStops") {
      const arrivalTime = currentItem.arrivalTime;
      const departureTime = currentItem.departureTime;
      const toDate = (timeString) => (timeString ? new Date(timeString) : null);
      const newTime = toDate(value);
      const arrival = toDate(arrivalTime);
      const departure = toDate(departureTime);

      if (newTime) {
        if (arrival && newTime < arrival) {
          toastError(`${field} cannot be before arrival time`);
          return prevState;
        }

        if (departure && newTime > departure) {
          toastError(`${field} cannot be after departure time`);
          return prevState;
        }

        if (field === "laytimeStops" && currentLaytime.laytimeStarts) {
          const startTime = toDate(currentLaytime.laytimeStarts);
          if (startTime && newTime < startTime) {
            toastError("Laytime end cannot be before laytime start");
            return prevState;
          }
        }

        if (field === "laytimeStarts" && currentLaytime.laytimeStops) {
          const endTime = toDate(currentLaytime.laytimeStops);
          if (endTime && newTime > endTime) {
            toastError("Laytime start cannot be after laytime end");
            return prevState;
          }
        }
      }
    }
    currentItem.laytimeStatement = calculateFormData(
      currentLaytime,
      updatedData
    );

    updatedCargoList[selectedOperationIndex] = currentItem;

    setWarning((prev) => {
      const newWarning = { ...prev };
      if (newWarning[currentItemId]) {
        delete newWarning[currentItemId][field];
        if (Object.keys(newWarning[currentItemId]).length === 0) {
          delete newWarning[currentItemId];
        }
      }
      return newWarning;
    });

    return {
      ...newState,
      cargoList: updatedCargoList,
    };
  });
};

const updateEventTable = (
  setCargoOperationTableData,
  selectedOperationIndex,
  updatedEventTable
) => {
  setCargoOperationTableData((prevState) => {
    const newState = { ...prevState };
    const updatedCargoList = [...newState.cargoList];
    const currentItem = { ...updatedCargoList[selectedOperationIndex] };

    currentItem.laytimeStatement = {
      ...currentItem.laytimeStatement,
      eventTable: calculateTimeRows(updatedEventTable),
    };

    updatedCargoList[selectedOperationIndex] = currentItem;

    return {
      ...newState,
      cargoList: updatedCargoList,
    };
  });
};

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    margin: theme.spacing(3),
    maxWidth: "100%",
    overflowX: "auto",
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& th": {
      color: theme.palette.common.white,
      fontWeight: "bold",
    },
  },
  datePicker: {
    "& .MuiInputBase-root": {
      padding: "5px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      backgroundColor: "#eef2fe",
      fontSize: "0.875rem",
    },
    "& .MuiInputBase-input": {
      padding: 0,
    },
  },
  actionCell: {
    width: "120px",
  },
  addButton: {
    margin: theme.spacing(3),
    display: "flex",
    justifyContent: "flex-end",
  },
}));

const calculateTimeRows = (rawRows) => {
  const updatedRows = rawRows.map((row, i, arr) => {
    const prevToTime = i > 0 ? arr[i - 1].toTime : null;
    const nextToTime = i < arr.length - 1 ? arr[i + 1].toTime : null;

    const fromTimeRaw = row.fromTime || prevToTime;
    const toTimeRaw = row.toTime;

    const percent = Number(row.percentTimeToCount) || 0;

    const fromTime =
      fromTimeRaw && moment(fromTimeRaw).isValid() ? moment(fromTimeRaw) : null;
    let toTime =
      toTimeRaw && moment(toTimeRaw).isValid() ? moment(toTimeRaw) : null;

    let fromDay = "",
      toDay = "",
      timeDuration = 0,
      timeUsed = 0;

    if (fromTime) {
      fromDay = fromTime.format("ddd");
    }
    if (toTime) {
      toDay = toTime.format("ddd");
    }

    if (fromTime && toTime) {
      if (toTime.isBefore(fromTime)) {
        toastError("End date cannot be before start date");
        return row;
      }
    }

    if (toTime && nextToTime) {
      const nextToTimeMoment = moment(nextToTime);
      if (nextToTimeMoment.isValid() && toTime.isAfter(nextToTimeMoment)) {
        toastError("End date cannot be after the next row's end date");
        return row;
      }
    }

    if (fromTime && toTime) {
      timeDuration = +toTime.diff(fromTime, "days", true).toFixed(3);
      timeUsed = +(timeDuration * (percent / 100)).toFixed(3);
    }

    return {
      ...row,
      fromTime: fromTime ? fromTime.toISOString() : "",
      fromDay,
      toTime: toTime ? toTime.toISOString() : "",
      toDay,
      timeDuration,
      timeUsed,
    };
  });

  let cumulative = 0;
  return updatedRows.map((row) => {
    cumulative += row.timeUsed || 0;
    return {
      ...row,
      cumulatedTimeUsed: +cumulative.toFixed(3),
    };
  });
};

const calculateFormData = (prevData, newData) => {
  const cargoQty = parseFloat(newData.cargoQty) || 0;
  const dischargeRate = parseFloat(newData.dischargingRate) || 1;

  const totalTimeAllowed = (cargoQty / dischargeRate).toFixed(3);
  return {
    ...prevData,
    ...newData,
    totalTimeAllowed,
  };
};

const calculateSummaryDetails = (rows, laytimeStatementDetails) => {
  let lastNotCounted = 0;
  let lastDemurrageBeginTime = null;
  let demurrageStartSet = false;
  let lastDemurrageBegin = null;
  const totalTimeAllowedDays = parseFloat(
    laytimeStatementDetails.totalTimeAllowed || 0
  );

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const { timeDuration, timeUsed, fromTime, toTime } = row;

    let diff = 0;
    if (
      timeDuration !== undefined &&
      timeUsed !== undefined &&
      timeDuration !== null &&
      timeUsed !== null
    ) {
      diff = Number(timeDuration) - Number(timeUsed);
    }
    lastNotCounted = Number(diff.toFixed(3));

    if (fromTime && toTime) {
      let demurrageBegin;

      if (!demurrageStartSet) {
        const baseDate = new Date(toTime);
        const msToAdd = totalTimeAllowedDays * 24 * 60 * 60 * 1000;
        demurrageBegin = new Date(baseDate.getTime() + msToAdd);
        demurrageStartSet = true;
      } else {
        const prevDemurrageDate = new Date(lastDemurrageBegin);
        const msToAdd = lastNotCounted * 24 * 60 * 60 * 1000;
        demurrageBegin = new Date(prevDemurrageDate.getTime() + msToAdd);
      }
      lastDemurrageBegin = demurrageBegin.toISOString();
      lastDemurrageBeginTime = lastDemurrageBegin;
    }
  }
  return {
    vesselOnDemurrage: lastDemurrageBeginTime,
    lastNotCounted,
    lastDemurrageBeginTime,
  };
};

const storeLaytimeSummary = (
  setCargoOperationTableData,
  selectedOperationIndex,
  summary
) => {
  setCargoOperationTableData((prevState) => {
    const newState = { ...prevState };
    const updatedCargoList = [...newState.cargoList];
    const currentItem = { ...updatedCargoList[selectedOperationIndex] };
    currentItem.laytimeStatement = {
      ...currentItem.laytimeStatement,
      ...summary,
    };
    updatedCargoList[selectedOperationIndex] = currentItem;
    return {
      ...newState,
      cargoList: updatedCargoList,
    };
  });
};

const LaytimeStatement = ({
  laytimeStatementDetails,
  isReadOnly = false,
  setCargoOperationTableData,
  selectedOperationIndex,
  warning,
  currentItemId,
  setWarning,
  onDownloadPdf,
}) => {
  const isDischarging = useRef(false);
  useEffect(() => {
    isDischarging.current = (laytimeStatementDetails?.operation?.toLowerCase() || "").trim() === "discharging";
  }, [laytimeStatementDetails.operation]);
  const classes = useStyles();
  const [openUploadPDF, setOpenUploadPDF] = useState(false);
  const [openUploadHTML, setOpenUploadHTML] = useState(false);
  const inputFileRef = useRef(null);

  const onButtonClick = () => {
    setOpenUploadHTML(true);
  };

  const laytimeSummary = useMemo(() => {
    const totalDuration = laytimeStatementDetails.eventTable?.reduce(
      (sum, row) => sum + (parseFloat(row.timeDuration) || 0),
      0
    );
    const totalTimeUsed = laytimeStatementDetails.eventTable?.reduce(
      (sum, row) => sum + (parseFloat(row.timeUsed) || 0),
      0
    );
    const totalTimeAllowed =
      parseFloat(laytimeStatementDetails.totalTimeAllowed) || 0;
    const difference = totalTimeUsed - totalTimeAllowed;
    const demurrage =
      difference > 0
        ? difference * (parseFloat(laytimeStatementDetails.despatchRate) || 0)
        : difference * (parseFloat(totalTimeAllowed) || 0);

    return {
      totalDuration,
      totalTimeUsed,
      totalTimeAllowed,
      difference,
      demurrage,
    };
  }, [
    laytimeStatementDetails.eventTable,
    laytimeStatementDetails.totalTimeAllowed,
    laytimeStatementDetails.despatchRate,
    laytimeStatementDetails.demurrageRate,
  ]);

  // Store laytime summary in laytimeStatement in cargoList
  useEffect(() => {
    storeLaytimeSummary(
      setCargoOperationTableData,
      selectedOperationIndex,
      laytimeSummary
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laytimeSummary, setCargoOperationTableData, selectedOperationIndex]);

  const summaryDetails = useMemo(() => {
    return calculateSummaryDetails(
      laytimeStatementDetails.eventTable || [],
      laytimeStatementDetails
    );
  }, [laytimeStatementDetails]);

  useEffect(() => {
    if (summaryDetails.vesselOnDemurrage) {
      updateCargoOperationData({
        setCargoOperationTableData,
        selectedOperationIndex,
        field: "vesselOnDemurrage",
        value: summaryDetails.vesselOnDemurrage,
      });
    }
  }, [
    summaryDetails.vesselOnDemurrage,
    selectedOperationIndex,
    setCargoOperationTableData,
  ]);

  const handleInputChange = useCallback(
    (id, field, value) => {
      const updatedEventTable =
        laytimeStatementDetails.eventTable?.map((row) =>
          row.id === id ? { ...row, [field]: value } : row
        ) || [];

      updateEventTable(
        setCargoOperationTableData,
        selectedOperationIndex,
        updatedEventTable
      );
    },
    [
      setCargoOperationTableData,
      selectedOperationIndex,
      laytimeStatementDetails.eventTable,
    ]
  );

  const handleDateChange = useCallback(
    (id, field, date, index) => {
      const laytimeStart = laytimeStatementDetails?.laytimeStarts;
      const departureTime = laytimeStatementDetails?.departureTime;

      if (!laytimeStart) {
        toastError("Please set both laytime start and end times");
        return;
      }

      const toMoment = (time) => (time ? moment(time) : null);
      const newTime = toMoment(date);
      const laytimeStarts = toMoment(laytimeStart);
      const departureTimes = toMoment(departureTime);

      const eventTable = laytimeStatementDetails.eventTable || [];
      // Create shallow copy of rows (to preserve immutability)
      let updatedEventTable = [...eventTable];

      if (index < 0 || index >= updatedEventTable.length) {
        // Invalid index, just return early
        return;
      }

      const currentRow = { ...updatedEventTable[index] };
      const currentFromTime = toMoment(currentRow.fromTime);
      const currentToTime = toMoment(currentRow.toTime);

      // Validation for fromTime
      if (field === "fromTime" && newTime) {
        if (laytimeStarts && newTime.isBefore(laytimeStarts)) {
          toastError("From time cannot be before laytime start time");
          return;
        }
        if (departureTimes && newTime.isAfter(departureTimes)) {
          toastError("From time cannot be after departure time");
          return;
        }
      }

      // Validation for toTime
      if (field === "toTime" && newTime) {
        if (laytimeStarts && newTime.isBefore(laytimeStarts)) {
          toastError("To time cannot be before laytime start time");
          return;
        }
        if (departureTimes && newTime.isAfter(departureTimes)) {
          toastError("To time cannot be after departure time");
          return;
        }
        if (currentFromTime && newTime.isBefore(currentFromTime)) {
          toastError("To time cannot be before from time");
          return;
        }

        // New validation: current row toTime must not be greater than next row toTime
        if (index < updatedEventTable.length - 1) {
          const nextRow = updatedEventTable[index + 1];
          const nextToTime = toMoment(nextRow.toTime);
          if (nextToTime && newTime.isAfter(nextToTime)) {
            toastError("Current row toTime cannot be after next row toTime");
            return;
          }
        }
      }

      // Update current row immutably with new field value
      currentRow[field] = date;
      updatedEventTable[index] = currentRow;

      // If field is "toTime", update next row's fromTime immutably
      if (field === "toTime" && date && index < updatedEventTable.length - 1) {
        const nextRow = { ...updatedEventTable[index + 1], fromTime: date };
        updatedEventTable[index + 1] = nextRow;
      }

      setWarning((prev) => {
        const newWarning = { ...prev };

        if (
          newWarning[currentItemId] &&
          newWarning[currentItemId].eventTable &&
          newWarning[currentItemId].eventTable[index]
        ) {
          delete newWarning[currentItemId].eventTable[index][field];

          if (
            Object.keys(newWarning[currentItemId].eventTable[index]).length === 0
          ) {
            delete newWarning[currentItemId].eventTable[index];
          }
          if (Object.keys(newWarning[currentItemId].eventTable).length === 0) {
            delete newWarning[currentItemId].eventTable;
          }
          if (Object.keys(newWarning[currentItemId]).length === 0) {
            delete newWarning[currentItemId];
          }
        }

        return newWarning;
      });

      updateEventTable(
        setCargoOperationTableData,
        selectedOperationIndex,
        updatedEventTable
      );
    },
    [setCargoOperationTableData, selectedOperationIndex, laytimeStatementDetails]
  );


  const addRow = useCallback(() => {
    const eventTable = laytimeStatementDetails.eventTable || [];
    const newId =
      eventTable.length > 0
        ? Math.max(...eventTable.map((row) => row.id)) + 1
        : 1;
    const lastRow =
      eventTable.length > 0 ? eventTable[eventTable.length - 1] : null;

    const newRow = {
      id: newId,
      fromTime: lastRow?.toTime || null,
      fromDay: lastRow?.toDay || "",
      toTime: null,
      toDay: "",
      event: "",
      timeDuration: 0,
      percentTimeToCount: 100,
      timeUsed: 0,
      cumulatedTimeUsed: 0,
      comments: "",
    };

    const updatedEventTable = [...eventTable, newRow];
    updateEventTable(
      setCargoOperationTableData,
      selectedOperationIndex,
      updatedEventTable
    );
  }, [
    laytimeStatementDetails.eventTable,
    selectedOperationIndex,
    setCargoOperationTableData,
  ]);

  const deleteRow = useCallback(
    (id) => {
      const updatedEventTable = laytimeStatementDetails.eventTable.filter(
        (row) => row.id !== id
      );
      updateEventTable(
        setCargoOperationTableData,
        selectedOperationIndex,
        updatedEventTable
      );
    },
    [
      laytimeStatementDetails.eventTable,
      selectedOperationIndex,
      setCargoOperationTableData,
    ]
  );

  const handleChange = useCallback(
    (field, value) => {
      updateCargoOperationData({
        setCargoOperationTableData,
        selectedOperationIndex,
        field,
        value,
        setWarning,
        currentItemId,
      });
    },
    [selectedOperationIndex, setCargoOperationTableData]
  );

  function getInputValue(doc, id) {
    const el = doc.getElementById(id);
    if (!el) {
      console.warn(`Element with id=${id} not found`);
      return "";
    }
    if (el.tagName.toUpperCase() === "INPUT" || el.tagName.toUpperCase() === "TEXTAREA") {
      return el.value || "";
    }
    return el.textContent || "";
  }


  function getDateValue(doc, id) {
    const el = doc.getElementById(id);
    if (!el) return null;

    const value = el.value || el.textContent || el.innerText || "";
    if (!value) return null;

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  const getNumberValue = (doc, id) => {
    const val = getInputValue(doc, id);
    if (!val || val.trim() === "") return null;

    const num = parseFloat(val);
    return isNaN(num) ? null : num;
  };

  function getSpanText(doc, id) {
    const el = doc.getElementById(id);
    if (!el) return "";
    return (el.textContent || el.innerText || "").trim();
  }

  const parseHtmlFile = async (file) => {
    try {
      const text = await file.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");

      // Check if parsing was successful
      if (!doc || doc.querySelector('parsererror')) {
        throw new Error('Failed to parse HTML file');
      }

      const vesselData = {
        vesselName: getInputValue(doc, "vesselName"),
        cargoDesc: getInputValue(doc, "cargoDesc"),
        billOfLadingDate: getDateValue(doc, "billOfLadingDate"),
        voyageNo: getInputValue(doc, "voyageNo"),
        charterer: getInputValue(doc, "charterer"),
        laytimePort: getInputValue(doc, "laytimePort"),
        operation: getInputValue(doc, "operation"),
        shipmentId: getInputValue(doc, "shipmentId"),
        cargoQty: getNumberValue(doc, "cargoQty"),
        dischargingRate: getNumberValue(doc, "dischargingRate"),
        dischargingTerms: getInputValue(doc, "dischargingTerms"),
        turnTimeAllowed: getNumberValue(doc, "turnTimeAllowed"),
        turnTimeClause: getInputValue(doc, "turnTimeClause"),
        demurrageRate: getNumberValue(doc, "demurrageRate"),
        despatchRate: getNumberValue(doc, "despatchRate"),
        totalTimeAllowed: getNumberValue(doc, "totalTimeAllowed"),
        arrivalTime: getDateValue(doc, "arrivalTime"),
        norTendered: getDateValue(doc, "norTendered"),
        norTerms: getInputValue(doc, "norTerms"),
        laytimeStarts: getDateValue(doc, "laytimeStarts"),
        commencedDischarging: getDateValue(doc, "commencedDischarging"),
        completedDischarging: getDateValue(doc, "completedDischarging"),
        laytimeStops: getDateValue(doc, "laytimeStops"),
        vesselOnDemurrage: getDateValue(doc, "vesselOnDemurrage"),
        departureTime: getDateValue(doc, "departureTime"),
      };

      // Extract event table rows
      const eventTableBody = doc.querySelector("#eventTableBody");
      let eventTable = [];

      if (eventTableBody) {
        const eventRows = Array.from(eventTableBody.querySelectorAll("tr"));
        eventTable = eventRows.map((tr) => {
          const tds = tr.querySelectorAll("td");
          if (tds.length < 10) return null; // Minimum expected columns

          // Helper function to safely get input value from td
          const getInputFromTd = (td, inputType = "input") => {
            if (!td) return "";
            const input = td.querySelector(inputType);
            return input ? (input.value || "") : (td.textContent || td.innerText || "").trim();
          };

          // Helper function to get text content from td
          const getTextFromTd = (td) => {
            if (!td) return "";
            return (td.textContent || td.innerText || "").trim();
          };

          return {
            fromTime: getInputFromTd(tds[1]),
            fromDay: getTextFromTd(tds[2]),
            toTime: getInputFromTd(tds[3]),
            toDay: getTextFromTd(tds[4]),
            event: getInputFromTd(tds[5]),
            timeDuration: getTextFromTd(tds[6]),
            percentTimeToCount: getInputFromTd(tds[7]),
            timeUsed: getTextFromTd(tds[8]),
            cumulatedTimeUsed: getTextFromTd(tds[9]),
            comments: tds[10] ? getInputFromTd(tds[10]) : "",
          };
        }).filter(Boolean); // Remove null entries
      }

      // Extract summary values
      const summary = {
        totalDuration: getSpanText(doc, "totalDuration"),
        totalTimeUsed: getSpanText(doc, "totalTimeUsed"),
        totalTimeAllowedSummary: getSpanText(doc, "totalTimeAllowedSummary"),
        difference: getSpanText(doc, "difference"),
        demurrage: getSpanText(doc, "demurrage"),
      };

      return {
        ...vesselData,
        eventTable,
        summary,
      };

    } catch (error) {
      console.error('Error parsing HTML file:', error);
      throw new Error(`Failed to parse HTML file: ${error.message}`);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const data = await parseHtmlFile(file);
      setCargoOperationTableData((prev) => {
        const newState = { ...prev };
        const updatedCargoList = [...newState?.cargoList];
        const currentItem = { ...updatedCargoList[selectedOperationIndex] };
        const currentLaytimeStatement = currentItem?.laytimeStatement;

        if (currentLaytimeStatement) {
          const laytimeStart = moment(data?.laytimeStarts);
          const laytimeStop = moment(data?.laytimeStops);
          const eventTable = data?.eventTable;
          const arrivalTime = moment(currentItem?.arrivalTime);
          const departureTime = moment(currentItem?.departureTime);

          const newWarnings = { ...warning };
          let toastWarnings = [];

          if (!newWarnings[currentItem?._id]) {
            newWarnings[currentItem._id] = {};
          }

          if (laytimeStart.isBefore(arrivalTime)) {
            newWarnings[currentItem._id].laytimeStarts =
              "Laytime Start cannot be earlier than Arrival Time";
            toastWarnings.push("Laytime Start cannot be earlier than Arrival Time");
          } else if (laytimeStart.isAfter(departureTime)) {
            newWarnings[currentItem._id].laytimeStarts =
              "Laytime Start cannot be later than Departure Time";
            toastWarnings.push("Laytime Start cannot be later than Departure Time");
          }

          if (laytimeStop.isBefore(arrivalTime)) {
            newWarnings[currentItem._id].laytimeStops =
              "Laytime Stop cannot be earlier than Arrival Time";
            toastWarnings.push("Laytime Stop cannot be earlier than Arrival Time");
          } else if (laytimeStop.isAfter(departureTime)) {
            newWarnings[currentItem._id].laytimeStops =
              "Laytime Stop cannot be later than Departure Time";
            toastWarnings.push("Laytime Stop cannot be later than Departure Time");
          }

          // Validate event rows
          eventTable.forEach((evt, idx) => {
            const fromM = moment(evt.fromTime);
            const toM = moment(evt.toTime);
            let rowWarns = {};

            if (fromM.isBefore(arrivalTime)) {
              rowWarns.fromTime = "Event ‘fromTime’ cannot be before Arrival Time";
            } else if (fromM.isAfter(departureTime)) {
              rowWarns.fromTime = "Event ‘fromTime’ cannot be after Departure Time";
            }

            if (toM.isBefore(arrivalTime)) {
              rowWarns.toTime = "Event ‘toTime’ cannot be before Arrival Time";
            } else if (toM.isAfter(departureTime)) {
              rowWarns.toTime = "Event ‘toTime’ cannot be after Departure Time";
            }

            if (Object.keys(rowWarns).length > 0) {
              if (!newWarnings[currentItem._id].eventTable) {
                newWarnings[currentItem._id].eventTable = {};
              }
              newWarnings[currentItem._id].eventTable[idx] = rowWarns;
              Object.values(rowWarns).forEach(msg =>
                toastWarnings.push(`Row ${idx + 1}: ${msg}`)
              );
            }
          });

          // Display warning toast if issues found
          if (toastWarnings.length > 0) {
            const formattedToast = (
              <div>
                <strong className="text-rose-600">⚠️ Validation Warning</strong>
                <ul className="mt-1 list-disc list-inside text-sm text-rose-700">
                  {toastWarnings.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </div>
            );
            toast.error(formattedToast, {
              autoClose: false,
              position: "bottom-left",
              className:
                "max-w-sm sm:max-w-md md:max-w-2xl w-[450px] text-black border border-rose-300 rounded-md p-4 bg-rose-100 shadow-lg",
              bodyClassName: "overflow-x-auto",
            });
          }

          // Apply warnings to UI state
          setWarning(newWarnings);
        }
        const { vesselName, arrivalTime, departureTime, cargoDesc, voyageNo, shipmentId, operation, laytimePort, charterer, billOfLadingDate, ...rest } = data;
        currentItem.laytimeStatement = rest;
        updatedCargoList[selectedOperationIndex] = currentItem;
        return {
          ...newState,
          cargoList: updatedCargoList,
        };
      });
    } catch (err) {
      toastError("Failed to parse file.");
    }
  };

  const renderDatePicker = useCallback(
    (field, label) => {
      const rawVal = laytimeStatementDetails[field];
      const validVal = rawVal && moment(rawVal).isValid() ? moment(rawVal) : null;
      const warningMessage = warning && warning[currentItemId] ? warning[currentItemId][field] : null;

      return (
        <tr className="hover:bg-[#ffffcc]/60">
          <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-1.5 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
            {label}
          </td>
          <td className="w-1/2 border-b border-slate-300 p-0 align-middle bg-[#ffffd6] h-[19px]">
            <div className="flex items-center w-full h-[19px]">
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <Tooltip
                  title={
                    <span style={{ fontSize: "0.73rem" }}>
                      {validVal
                        ? `Selected: ${validVal.format("DD/MM/YYYY HH:mm")}`
                        : "Select date and time"}
                    </span>
                  }
                  placement="top"
                  arrow
                >
                  <KeyboardDateTimePicker
                    size="small"
                    variant="dialog"
                    ampm={false}
                    margin="none"
                    style={{ width: "100%", height: "19px", margin: 0, padding: 0 }}
                    className="laytime-datepicker w-full bg-transparent h-[19px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[19px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[19px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[19px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-1.5 [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-normal [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-0 [&_.MuiInputAdornment-root]:!h-[19px] [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[19px] [&_.MuiIconButton-root]:!w-[19px] [&_.MuiSvgIcon-root]:!w-3 [&_.MuiSvgIcon-root]:!h-3 [&_.MuiSvgIcon-root]:!text-[10px]"
                    format="DD/MM/YYYY HH:mm"
                    value={validVal}
                    onChange={(date) => handleChange(field, date)}
                    placeholder="DD/MM/YYYY HH:mm"
                    inputProps={{
                      style: {
                        backgroundColor: "transparent",
                        height: "19px",
                        minHeight: "19px",
                        maxHeight: "19px",
                        padding: "0 4px",
                        fontSize: "11px",
                        lineHeight: "normal",
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      style: {
                        fontSize: "11px",
                        padding: "0 2px",
                        height: "19px",
                        minHeight: "19px",
                        maxHeight: "19px",
                        backgroundColor: "transparent",
                      },
                    }}
                    error={false}
                    helperText={null}
                    FormHelperTextProps={{ style: { display: "none" } }}
                    onError={() => { }}
                    keyboardIcon={<CalendarTodayIcon color="action" style={{ fontSize: "0.55rem" }} />}
                    KeyboardButtonProps={{ style: { padding: 0, margin: 0 } }}
                  />
                </Tooltip>
              </MuiPickersUtilsProvider>
              {warningMessage && (
                <Tooltip title={warningMessage} placement="top" arrow>
                  <InfoOutlined color="error" style={{ fontSize: "0.85rem", marginLeft: "2px" }} />
                </Tooltip>
              )}
            </div>
          </td>
        </tr>
      );
    },
    [laytimeStatementDetails, handleChange, warning, currentItemId]
  );

  const renderTabelDatePicker = useCallback(
    (id, field, value, index) => {
      const validVal = value && moment(value).isValid() ? moment(value) : null;
      return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDateTimePicker
            value={validVal}
            onChange={(date) => handleDateChange(id, field, date, index)}
            format="DD/MM/YYYY HH:mm"
            placeholder="DD/MM/YYYY HH:mm"
            ampm={false}
            margin="none"
            style={{ width: "100%", height: "19px", margin: 0, padding: 0 }}
            className="laytime-datepicker w-full bg-transparent h-[19px] text-[11px] text-slate-900 !m-0 !p-0 [&_.MuiFormControl-root]:!m-0 [&_.MuiFormControl-root]:!p-0 [&_.MuiFormControl-root]:!h-[19px] [&_.MuiFormControl-root]:!flex-row [&_.MuiFormControl-root]:!items-center [&_.MuiInputBase-root]:!h-[19px] [&_.MuiInputBase-root]:!flex-row [&_.MuiInputBase-root]:!items-center [&_.MuiInputBase-root]:!w-full [&_.MuiInputBase-root]:!min-h-0 [&_.MuiInputBase-root]:!bg-transparent [&_.MuiInputBase-root]:!bg-none [&_.MuiInputBase-input]:!h-[19px] [&_.MuiInputBase-input]:!py-0 [&_.MuiInputBase-input]:!px-1.5 [&_.MuiInputBase-input]:!text-[11px] [&_.MuiInputBase-input]:!leading-[19px] [&_.MuiInputBase-input]:!bg-transparent [&_input]:!bg-transparent [&_input[readonly]]:!bg-transparent [&_input::placeholder]:!text-[9.5px] [&_input::placeholder]:!text-slate-400 [&_.MuiInput-underline]:before:!border-none [&_.MuiInput-underline]:after:!border-none [&_.MuiInputAdornment-root]:!ml-0 [&_.MuiInputAdornment-root]:!h-[19px] [&_.MuiIconButton-root]:!p-0 [&_.MuiIconButton-root]:!h-[19px] [&_.MuiIconButton-root]:!w-[19px] [&_.MuiSvgIcon-root]:!w-3 [&_.MuiSvgIcon-root]:!h-3 [&_.MuiSvgIcon-root]:!text-[10px]"
            inputProps={{
              style: {
                backgroundColor: "transparent",
                height: "19px",
                minHeight: "19px",
                maxHeight: "19px",
                padding: "0 4px",
                fontSize: "11px",
                lineHeight: "19px",
              },
            }}
            InputProps={{
              readOnly: true,
              disableUnderline: true,
              style: {
                fontSize: "11px",
                padding: "0 2px",
                height: "19px",
                minHeight: "19px",
                maxHeight: "19px",
                backgroundColor: "transparent",
              },
            }}
            error={false}
            helperText={null}
            FormHelperTextProps={{
              style: { display: "none" },
            }}
            onError={() => { }}
            keyboardIcon={
              <CalendarTodayIcon color="action" style={{ fontSize: "0.55rem" }} />
            }
            KeyboardButtonProps={{ style: { padding: 0, margin: 0 } }}
          />
        </MuiPickersUtilsProvider>
      );
    },
    [handleDateChange]
  );

  const renderInput = useCallback(
    (field, label, type = "text", isDisabled) => {
      const rawValue = laytimeStatementDetails[field];
      let displayValue = rawValue || "";
      if (
        field === "vesselOnDemurrage" ||
        field === "arrivalTime" ||
        field === "departureTime"
      ) {
        displayValue =
          rawValue && moment(rawValue).isValid()
            ? moment(rawValue).format("DD/MM/YYYY HH:mm")
            : "-";
      }

      return (
        <tr className="hover:bg-[#ffffcc]/60">
          <td className="w-1/2 border-r border-b border-slate-300 bg-slate-50 px-1.5 py-0 text-[11px] font-medium text-slate-800 align-middle truncate h-[19px]">
            {label}
          </td>
          <td className={`w-1/2 border-b border-slate-300 p-0 align-middle h-[19px] ${isDisabled ? "bg-slate-100" : "bg-[#ffffd6]"}`}>
            <input
              type={type}
              value={displayValue}
              onChange={(e) => handleChange(field, e.target.value)}
              onWheel={(e) => e.target.blur()}
              disabled={isDisabled}
              className={`w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none border-none font-medium ${isDisabled
                ? "bg-slate-100 text-slate-700 cursor-not-allowed"
                : "bg-[#ffffd6] text-slate-900 focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600"
                }`}
            />
          </td>
        </tr>
      );
    },
    [laytimeStatementDetails, handleChange]
  );

  return (
    <div className="space-y-1">
      {!isReadOnly && (
        <div className="flex items-center justify-end gap-1.5 mb-1">
          <DownloadPdf
            isLaytimeStatement
            pdfData={{
              ...laytimeStatementDetails,
              ...laytimeSummary,
            }}
            onDownloadPdf={onDownloadPdf}
          />
          <button
            onClick={() => setOpenUploadPDF(true)}
            className="flex items-center gap-1.5 px-2.5 py-0.5 h-[22px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none cursor-pointer hover:bg-[#0c2f50] transition-colors"
          >
            <FaFileUpload size={13} />
            <span>Upload PDF</span>
          </button>
          <input
            ref={inputFileRef}
            type="file"
            accept=".html,.htm"
            style={{ display: "none" }}
            onChange={(event) => { setOpenUploadHTML(false); handleFile(event); }}
          />
          <button
            onClick={onButtonClick}
            className="flex items-center gap-1.5 px-2.5 py-0.5 h-[22px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none cursor-pointer hover:bg-[#0c2f50] transition-colors"
          >
            <BsFiletypeHtml size={13} />
            <span>Upload HTML</span>
          </button>
        </div>
      )}
      {laytimeStatementDetails.vesselName && (
        <div className="text-center mb-1">
          <h4 className="font-medium text-[11px] uppercase tracking-wider text-[#313C4C] m-0">
            Laytime Statement for{" "}
            <span className="text-slate-900 font-semibold">
              {laytimeStatementDetails.vesselName}
            </span>{" "}
            B/L Dated{" "}
            <span className="text-slate-900 font-semibold">
              {laytimeStatementDetails.billOfLadingDate &&
                moment(laytimeStatementDetails.billOfLadingDate).isValid()
                ? moment(laytimeStatementDetails.billOfLadingDate).format("DD-MMM-YYYY")
                : "N/A"}
            </span>
            : {laytimeStatementDetails.shipmentId || "N/A"} For{" "}
            <span className="text-slate-900 font-semibold">
              {laytimeStatementDetails.laytimePort || "N/A"}
            </span>{" "}
            - {laytimeStatementDetails.operation || "N/A"}
          </h4>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5 mb-1.5">
        {/* First Column - Vessel Info */}
        <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
          <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 capitalize tracking-wider text-center">
            Vessel Info
          </div>
          <table className="w-full text-left border-collapse table-fixed">
            <tbody>
              {renderInput("vesselName", "Vessel Name", "text", true)}
              {renderInput("cargoDesc", "Cargo Desc", "text", true)}
              {renderInput("billOfLadingDate", "B/L Date", "date", true)}
              {renderInput("voyageNo", "Voyage No", "text", true)}
              {renderInput("charterer", "Charterer", "text", true)}
              {renderInput("port", "Laytime for Port", "text", true)}
              {renderInput("operation", "Operation", "text", true)}
              {renderInput("shipmentId", "Shipment ID", "text", true)}
            </tbody>
          </table>
        </div>

        {/* Second Column - Terms & Rates */}
        <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
          <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 capitalize tracking-wider text-center">
            Terms & Rates
          </div>
          <table className="w-full text-left border-collapse table-fixed">
            <tbody>
              {renderInput("cargoQty", "Cargo Quantity (MT)", "number")}
              {renderInput(
                "dischargingRate",
                isDischarging.current ? "Discharging Rate" : "Loading Rate",
                "number"
              )}
              {renderInput(
                "dischargingTerms",
                isDischarging.current ? "Discharging Terms" : "Loading Terms",
                "text"
              )}
              {renderInput("turnTimeAllowed", "Turn Time Allowed", "number")}
              {renderInput("turnTimeClause", "Turn Time Clause", "text")}
              {renderInput("demurrageRate", "Demurrage Rate ($/day)", "number")}
              {renderInput("despatchRate", "Despatch Rate ($/day)", "number")}
              {renderInput(
                "totalTimeAllowed",
                "Total Time Allowed",
                "number",
                true
              )}
            </tbody>
          </table>
        </div>

        {/* Third Column - Time Marks */}
        <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs">
          <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 capitalize tracking-wider text-center">
            Time Marks
          </div>
          <table className="w-full text-left border-collapse table-fixed">
            <tbody>
              {renderInput("arrivalTime", "Arrival time", "text", true)}
              {renderDatePicker("norTendered", "NOR Tendered")}
              {renderInput("norTerms", "NOR Terms", "text")}
              {renderDatePicker(
                "laytimeStarts",
                "Laytime Starts",
                warning?.[currentItemId]?.laytimeStarts || ""
              )}
              {renderDatePicker(
                "commencedDischarging",
                isDischarging.current ? "Commenced Discharging" : "Commenced Loading"
              )}
              {renderDatePicker(
                "completedDischarging",
                isDischarging.current ? "Completed Discharging" : "Completed Loading"
              )}
              {renderDatePicker(
                "laytimeStops",
                "Laytime Stops",
                warning?.[currentItemId]?.laytimeStops || ""
              )}
              {renderInput(
                "vesselOnDemurrage",
                "Vessel on Demurrage",
                "text",
                true
              )}
              {renderInput("departureTime", "Departure Time", "text", true)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Events Table Section */}
      <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs mb-1.5">
        <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 capitalize tracking-wider">
          Events Table
        </div>
        {(warning?.[currentItemId]?.laytimeStarts ||
          warning?.[currentItemId]?.laytimeStops) && (
            <div className="px-2 py-0.5 border-b border-slate-300 bg-rose-50 text-rose-700 text-[11px] flex items-center gap-1.5">
              <InfoOutlined color="error" style={{ fontSize: "0.85rem" }} />
              <div>
                The <strong>Laytime Start</strong> or{" "}
                <strong>Laytime Stop</strong> values are invalid. This may cause{" "}
                <strong>incorrect event details</strong> to be shown in the
                table.
              </div>
            </div>
          )}

        <div className="overflow-x-auto scroll-thin w-full">
          <table className="w-full text-left border-collapse border-b border-slate-300 table-fixed min-w-[1050px]">
            <thead>
              <tr>
                {!isReadOnly && (
                  <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[32px]">
                    Add
                  </th>
                )}
                <th className="px-1 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[115px]">
                  From Time
                </th>
                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[35px]">
                  Day
                </th>
                <th className="px-1 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[125px]">
                  To Time
                </th>
                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[35px]">
                  Day
                </th>
                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle min-w-[160px]">
                  Event
                </th>
                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                  Duration (days)
                </th>
                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                  % Time to Count
                </th>
                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                  Time Used (days)
                </th>
                <th className="px-1 py-0.5 text-center border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle whitespace-normal leading-tight w-[95px]">
                  Cumulated Time
                </th>
                <th className="px-1.5 py-0.5 text-left border-r border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle min-w-[130px]">
                  Comments
                </th>
                {!isReadOnly && (
                  <th className="px-1 py-0.5 text-center border-b border-slate-300 text-[11px] font-semibold text-slate-800 bg-[#f0f2f5] align-middle w-[52px]">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {laytimeStatementDetails.eventTable?.length > 0 ? (
                laytimeStatementDetails.eventTable.map((row, index) => {
                  const rowWarnings =
                    warning?.[currentItemId]?.eventTable?.[index] || {};
                  const isAfterDeparture =
                    (row.fromTime &&
                      laytimeStatementDetails.vesselOnDemurrage &&
                      new Date(row.fromTime) >
                      new Date(laytimeStatementDetails.vesselOnDemurrage)) ||
                    (row.toTime &&
                      laytimeStatementDetails.vesselOnDemurrage &&
                      new Date(row.toTime) >
                      new Date(laytimeStatementDetails.vesselOnDemurrage));
                  return (
                    <tr key={row.id} className="hover:bg-[#ffffcc]/60">
                      {!isReadOnly && (
                        <td className="border border-slate-300 p-0 text-center bg-slate-50 align-middle">
                          <div className="flex items-center justify-center h-[19px]">
                            <button
                              onClick={addRow}
                              className="p-0 text-blue-600 hover:text-blue-800 transition cursor-pointer"
                              title="Add row"
                            >
                              <BiPlus style={{ width: "9px", height: "9px", minWidth: "9px", minHeight: "9px" }} />
                            </button>
                          </div>
                        </td>
                      )}
                      <td className={`border border-slate-300 p-0 align-middle ${isAfterDeparture ? "bg-rose-50 text-rose-700 font-semibold" : "bg-slate-50"}`}>
                        <div className="h-[19px] px-1.5 flex items-center gap-1 text-[11px] font-medium text-slate-800 whitespace-nowrap">
                          <span>
                            {row.fromTime && moment(row.fromTime).isValid()
                              ? moment(row.fromTime).format("DD/MM/YYYY HH:mm")
                              : "-"}
                          </span>
                          {rowWarnings.fromTime && (
                            <Tooltip
                              placement="top"
                              arrow
                              title={
                                <span style={{ fontSize: "0.675rem" }}>
                                  {rowWarnings.fromTime}
                                </span>
                              }
                            >
                              <span className="text-rose-500" title={rowWarnings.fromTime}>
                                <InfoOutlined color="error" style={{ fontSize: "0.85rem" }} />
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </td>

                      <td className="border border-slate-300 p-0 text-center bg-slate-50 align-middle">
                        <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-600 uppercase">
                          {row.fromDay || "-"}
                        </div>
                      </td>

                      <td className={`border border-slate-300 p-0 align-middle ${isAfterDeparture ? "bg-rose-50" : "bg-[#ffffd6]"}`}>
                        <div className="flex items-center gap-1 w-full">
                          {renderTabelDatePicker(
                            row.id,
                            "toTime",
                            row.toTime,
                            index
                          )}
                          {rowWarnings.toTime && (
                            <Tooltip
                              title={
                                <span style={{ fontSize: "0.675rem" }}>
                                  {rowWarnings.toTime}
                                </span>
                              }
                              placement="top"
                              arrow
                            >
                              <InfoOutlined color="error" style={{ fontSize: "0.85rem" }} />
                            </Tooltip>
                          )}
                        </div>
                      </td>

                      <td className="border border-slate-300 p-0 text-center bg-slate-50 align-middle">
                        <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-600 uppercase">
                          {row.toDay || "-"}
                        </div>
                      </td>

                      <td className="border border-slate-300 p-0 align-middle bg-[#ffffd6]">
                        <input
                          value={row.event || ""}
                          onChange={(e) =>
                            handleInputChange(row.id, "event", e.target.value)
                          }
                          className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-gray-400"
                          placeholder="Enter event..."
                        />
                      </td>

                      <td className="border border-slate-300 p-0 align-middle bg-slate-100 text-center">
                        <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                          {row.timeDuration !== undefined && row.timeDuration !== null ? row.timeDuration : "0"}
                        </div>
                      </td>

                      <td className="border border-slate-300 p-0 align-middle bg-[#ffffd6] text-center">
                        <input
                          type="number"
                          value={row.percentTimeToCount !== undefined ? row.percentTimeToCount : ""}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) =>
                            handleInputChange(
                              row.id,
                              "percentTimeToCount",
                              e.target.value
                            )
                          }
                          min={0}
                          max={100}
                          className="w-full h-[19px] text-[11px] px-1.5 py-0 text-center block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium"
                          placeholder="0"
                        />
                      </td>

                      <td className="border border-slate-300 p-0 align-middle bg-slate-100 text-center">
                        <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                          {row.timeUsed !== undefined && row.timeUsed !== null ? row.timeUsed : "0"}
                        </div>
                      </td>

                      <td className="border border-slate-300 p-0 align-middle bg-slate-100 text-center">
                        <div className="h-[19px] flex items-center justify-center text-[11px] font-medium text-slate-800">
                          {row.cumulatedTimeUsed !== undefined && row.cumulatedTimeUsed !== null ? row.cumulatedTimeUsed : "0"}
                        </div>
                      </td>

                      <td className="border border-slate-300 p-0 align-middle bg-[#ffffd6]">
                        <input
                          value={row.comments || ""}
                          onChange={(e) =>
                            handleInputChange(
                              row.id,
                              "comments",
                              e.target.value
                            )
                          }
                          className="w-full h-[19px] text-[11px] px-1.5 py-0 block outline-none focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-600 bg-[#ffffd6] text-slate-900 border-none font-medium placeholder:text-gray-400"
                          placeholder="Comments..."
                        />
                      </td>

                      {!isReadOnly && (
                        <td className="border border-slate-300 p-0 text-center bg-slate-50 align-middle">
                          <div className="flex items-center justify-center h-[19px]">
                            <button
                              onClick={() => deleteRow(row.id)}
                              className="p-0 text-rose-600 hover:text-rose-800 transition cursor-pointer"
                              title="Delete row"
                            >
                              <FiTrash2 style={{ width: "9px", height: "9px", minWidth: "9px", minHeight: "9px" }} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={12}
                    className="px-4 py-2 text-center text-slate-400 italic text-[11px] border border-slate-300"
                  >
                    {!isReadOnly ? (
                      <div className="flex flex-col items-center justify-center gap-1.5">
                        <span>No event logs added yet.</span>
                        <button
                          onClick={addRow}
                          className="px-2.5 py-0.5 text-[11px] font-medium bg-[#313C4C] text-white hover:bg-[#0d508f] transition cursor-pointer flex items-center gap-1"
                        >
                          <BiPlus size={14} />
                          <span>Add First Row</span>
                        </button>
                      </div>
                    ) : (
                      "No Data"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-1 flex w-full justify-end">
        <div className="border border-slate-300 bg-white overflow-hidden shadow-2xs w-full md:w-80">
          <div className="bg-[#f0f2f5] border-b border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 capitalize tracking-wider text-center">
            Laytime Summary
          </div>
          <table className="w-full text-left border-collapse table-fixed">
            <tbody>
              <tr className="hover:bg-[#ffffcc]/60">
                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-800 align-middle">
                  Total Duration:
                </td>
                <td className="w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle">
                  {laytimeSummary?.totalDuration?.toFixed(5) || 0} days
                </td>
              </tr>
              <tr className="hover:bg-[#ffffcc]/60">
                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-800 align-middle">
                  Total Time Used:
                </td>
                <td className="w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle">
                  {laytimeSummary?.totalTimeUsed?.toFixed(5) || 0} days
                </td>
              </tr>
              <tr className="hover:bg-[#ffffcc]/60">
                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-800 align-middle">
                  Total Time Allowed:
                </td>
                <td className="w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 text-right bg-[#ffffd6] align-middle">
                  {laytimeSummary?.totalTimeAllowed?.toFixed(5) || 0} days
                </td>
              </tr>
              <tr className="hover:bg-[#ffffcc]/60">
                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 align-middle">
                  Difference:
                </td>
                <td className={`w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-right bg-[#ffffd6] align-middle ${laytimeSummary?.difference > 0 ? "text-emerald-700" : "text-rose-700"
                  }`}>
                  {laytimeSummary?.difference?.toFixed(5) || 0} days
                </td>
              </tr>
              <tr className="hover:bg-[#ffffcc]/60">
                <td className="w-1/2 border border-slate-300 bg-slate-50 px-1.5 py-0 h-[19px] text-[11px] font-medium text-slate-900 align-middle">
                  Demurrage / Despatch:
                </td>
                <td className={`w-1/2 border border-slate-300 px-1.5 py-0 h-[19px] text-[11px] font-medium text-right bg-[#ffffd6] align-middle ${laytimeSummary?.difference > 0 ? "text-emerald-700" : "text-rose-700"
                  }`}>
                  {laytimeSummary?.demurrage?.toFixed(3) || 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <UploadPDFModal
        open={openUploadPDF}
        handleClose={() => setOpenUploadPDF(false)}
        title="Import Laytime Statement from PDF"
        isLaytimeStatement={true}
        selectedOperationIndex={selectedOperationIndex}
        setState={setCargoOperationTableData}
        warning={warning}
        setWarning={setWarning}
      />
      <HtmlImportGuide open={openUploadHTML} onClose={() => setOpenUploadHTML(false)} onChooseFile={() => inputFileRef.current?.click()} kind="laytime" />
    </div>
  );
};

export default LaytimeStatement;
